import { Button, Timeline, Tooltip } from "antd";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { observer } from "mobx-react";
import * as React from "react";
import { projectsDataStore } from "Stores";
import { dayjs } from "Config/dayjs";
import { I18N_NS } from "Config/i18n";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";

interface BcfIssueEventsProps {
  topicId: string;
  conflictHistory?: API_BcfServerTopicEvent[];
  isInImportMode?: boolean;
}

type BcfEventType = API_BcfServerTopicEventActionData["type"] | API_BcfServerCommentEventActionData["type"];
const eventTypeToLabel: {
  [key in BcfEventType]?: string;
} = {
  topic_created: "Issue created by {{author}}",
  title_updated: "{{author}} updated title",
  description_updated: "{{author}} updated description",
  status_updated: "{{author}} updated status",
  type_updated: "{{author}} updated type",
  priority_updated: "{{author}} updated priority",
  due_date_updated: "{{author}} updated due date",
  assigned_to_updated: "{{author}} updated assignee",
  label_added: "{{author}} added label",
  label_removed: "{{author}} removed label",
  stage_updated: "{{author}} updated stage",
  stage_added: "{{author}} added stage",
  stage_removed: "{{author}} removed stage",
  comment_created: "{{author}} added comment",
  comment_text_updated: "{{author}} updated comment",
};

const IssueEventAction = ({ data, author }: { data: BcfTopicOrCommentEvent["actions"]; author: string }) => {
  const { t } = useTranslation(I18N_NS.XKT_VIEWER);

  return (
    <div id="issue-events">
      {data.map((action) => (
        <div key={action.type}>
          {t(eventTypeToLabel[action.type], { author: author })}
          {action.value && (
            <div>
              <Tooltip
                title={action.value}
                trigger={["click"]}
                getTooltipContainer={() => document.getElementById("issue-events")}
              >
                <Button size="small" type="link">
                  {t("Show details")}
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

interface BcfTopicOrCommentEvent
  extends Omit<API_BcfServerTopicEvent, "actions">,
    Omit<API_BcfServerCommentEvent, "actions"> {
  actions: API_BcfServerCommentEventActionData[] | API_BcfServerTopicEventActionData[];
}

export const BcfIssueEvents = observer(({ topicId, conflictHistory, isInImportMode }: BcfIssueEventsProps) => {
  const [allEvents, setAllEvents] = React.useState<BcfTopicOrCommentEvent[] | API_BcfServerTopicEvent[]>([]);
  const fetchEvents = async (topicId: string) => {
    try {
      if (isInImportMode) {
        if (conflictHistory?.length) {
          setAllEvents(conflictHistory);
        }
      } else {
        const [topicEventsResponse, commentEventsResponse] = await Promise.all([
          API.getData<API_BcfServerTopicEvent[]>(
            API_URL.BCF_SERVER_EVENTS.bcfServerGetTopicEventsByTopicId(projectsDataStore.bcfGuid, topicId),
            { $orderby: "date asc" }
          ),
          API.getData<API_BcfServerCommentEvent[]>(
            API_URL.BCF_SERVER_EVENTS.bcfServerGetCommentEvents(projectsDataStore.bcfGuid),
            {
              $orderby: "date asc",
              $filter: `topic_guid eq '${topicId}'`,
            }
          ),
        ]);

        const combinedResponse = sortBy<BcfTopicOrCommentEvent>(
          [].concat(topicEventsResponse, commentEventsResponse),
          (o) => new Date(o.date).getTime()
        );
        setAllEvents(combinedResponse);
      }
    } catch (error) {
      throw error;
    }
  };

  React.useEffect(() => {
    fetchEvents(topicId);
  }, [topicId]);

  return (
    <div>
      <Timeline>
        {allEvents.map((record, idx) => (
          <Timeline.Item key={idx}>
            <div className="color-grey" style={{ fontSize: 12 }}>
              {dayjs(record.date).format("lll")} ({record.author})
            </div>
            <IssueEventAction data={record.actions} author={record.author} />
          </Timeline.Item>
        ))}
      </Timeline>
    </div>
  );
});
